'use client'

import { LuneTheme } from '@lune-fe/lune-ui-lib'
import Box from '@mui/material/Box'
import Link from '@mui/material/Link'
import Text from '@mui/material/Typography'
import InsettingCertificate from '@public/images/product/lune-carbon-insetting-section.png'
import SustyPage from '@public/images/susty-page.png'

import AccordionTile from '@/components/AccordionTile'
import Assessment from '@/components/Assessment'
import DownloadGuide from '@/components/DownloadGuide'
import GenericHero from '@/components/Hero/GenericHero'
import IllustratedMainLinks from '@/components/IllustratedMainLinks'
import LearnMoreTiles from '@/components/LearnMoreTiles'
import RequestDemo from '@/components/RequestDemo'
import TextWithImageGoingOffScreen from '@/components/TextWithImageGoingOffScreen/TextWithImageGoingOffScreen'
import FundClimateProjectsLogos from '@/sections/FundClimateProjectsLogos'
import { HUBSPOT_FORM_IDS } from '@/utills/constants'

function CarbonProjectsFaq() {
    const { palette } = LuneTheme
    return (
        <>
            <Text variant={'h6'} sx={{ mb: 5 }}>
                FAQs
            </Text>
            <AccordionTile
                title={`What is carbon insetting?`}
                description={`Insetting means funding emission reductions within your own value chain. In logistics, this usually means paying for biofuel to replace fossil fuel in transport.`}
            />
            <AccordionTile
                title={`How does Book & Claim work?`}
                description={`The environmental benefit of the biofuel is separated from the physical fuel. The fuel is used somewhere in the logistics network, and you receive a verified certificate to claim the reduction. Lune follows Smart Freight Centre's Market-Based Measures Framework.`}
            />
            <AccordionTile
                title={`Which transport modes can be inset?`}
                description={`All of them: sea, air, road and rail.`}
            />
            <AccordionTile
                title={`How do you make sure biofuels are sustainable?`}
                description={`The fuels comply with ISCC EU certification and the EU RED II directive, and every certificate is third-party verified and registered with 123Carbon.`}
            />
            <AccordionTile
                title={`What is the difference between insetting and offsetting?`}
                description={`Insetting reduces emissions within your logistics value chain. Offsetting funds removal or avoidance projects outside it, for emissions you can't yet reduce.`}
            />
            <AccordionTile
                title={`How do you assess carbon projects?`}
                description={
                    <>
                        {`Every project must be third-party certified and pass Lune's own quality assessment. `}
                        <Link
                            href={'#project-quality-guide'}
                            sx={{
                                color: palette.Grey600,
                                textDecorationColor: palette.Grey600,
                            }}
                        >
                            Download our project quality guide
                        </Link>
                        {` for the full approach.`}
                    </>
                }
            />
        </>
    )
}

export default function CarbonProjectsPage() {
    return (
        <>
            <Box>
                <GenericHero
                    chips={{
                        items: [
                            {
                                src: '/images/heros/chip-fund-climate-projects-1.png',
                                width: 395,
                                height: 72,
                            },
                            {
                                src: '/images/heros/chip-fund-climate-projects-2.png',
                                width: 384,
                                height: 72,
                            },
                            {
                                src: '/images/heros/chip-fund-climate-projects-3.png',
                                width: 384,
                                height: 72,
                                sx: {
                                    ml: 9,
                                },
                            },
                            {
                                src: '/images/heros/chip-fund-climate-projects-4.png',
                                width: 406,
                                height: 72,
                            },
                        ],
                        gap: 9,
                    }}
                    backgroundImageMobile={'/images/heros/fund-climate-projects-mobile.png'}
                    imageAlt={
                        'Project quality checks for carbon projects, such as high-precision measurement and 100-year project lifetime, with one project failing a buffer check.'
                    }
                    title={'Take action on logistics emissions, with integrity'}
                    titleComponent={'h1'}
                    description={
                        "Reduce your logistics emissions with verified biofuel insetting across every transport mode, and address what remains with carbon projects that pass Lune's quality assessment."
                    }
                    button={{
                        href: '/request-a-demo',
                        label: 'Request a demo',
                    }}
                    secondaryButton={{
                        href: '#project-quality-guide',
                        label: 'Download our project quality guide',
                    }}
                />
                <FundClimateProjectsLogos caption={'Trusted by 100+ companies'} />
            </Box>
            <LearnMoreTiles
                tiles={[
                    {
                        title: 'Biofuel insetting',
                        description:
                            "Book & Claim under Smart Freight Centre's framework, for sea, air, road and rail.",
                        scrollToSection: 'biofuel-insetting',
                        icon: '/icons/lune-icon-biofuel.svg',
                    },
                    {
                        title: 'Verified and traceable',
                        description:
                            'Every certificate third-party verified and registered with 123Carbon.',
                        scrollToSection: 'biofuel-insetting',
                        icon: '/icons/icon-transparency.png',
                    },
                    {
                        title: 'High-quality offsetting',
                        description:
                            "Certified projects that also pass Lune's own quality assessment.",
                        scrollToSection: 'carbon-project-quality',
                        icon: '/icons/icon-high-quality-peace.png',
                    },
                    {
                        title: 'Built for your customers',
                        description:
                            'Logistics providers can offer insets and offsets in their own platform.',
                        scrollToSection: 'proof-of-impact',
                        icon: '/icons/icon-green-checkout.png',
                    },
                ]}
            />
            <Box id={'biofuel-insetting'}>
                <TextWithImageGoingOffScreen
                    title={'Reduce your logistics emissions with biofuel insetting'}
                    paragraph={
                        <>
                            {`Book & Claim lets you pay for biofuel used in the logistics network and claim the emissions reduction, even if the fuel doesn't power your specific shipment. Lune's insets follow Smart Freight Centre's Market-Based Measures Framework and cover sea, air, road and rail.`}
                            <br />
                            <br />
                            {`Every certificate is third-party verified and registered with 123Carbon.`}
                        </>
                    }
                    img={InsettingCertificate}
                    imgAlt={
                        'Example Lune insetting certificate for road freight: 1 tCO₂e reduced well-to-wheel through a switch to HVO made from used cooking oil, ISCC certified and registered with 123Carbon, available for ocean, air, road and rail freight.'
                    }
                />
            </Box>
            <Box id={'carbon-project-quality'}>
                <Assessment
                    hideDescriptions
                    title={'Carbon projects you can stand behind'}
                    introduction={
                        <>
                            {`For emissions you can't yet reduce, Lune offers a diverse selection of high-quality carbon removal and avoidance projects. Every project is third-party certified and must also pass Lune's own quality assessment, cross-checked against leading external ratings.`}
                            <br />
                            <br />
                            {`We assess every project on durability, additionality, measurability, co-benefits, risk mitigation and the delivery track record of its developer. Out of the hundreds of projects we vet, only the top 10% make the cut.`}
                        </>
                    }
                />
            </Box>
            <Box id={'project-quality-guide'} sx={{ scrollMarginTop: '120px' }}>
                <DownloadGuide hubspotFormId={HUBSPOT_FORM_IDS.greenForm} />
            </Box>
            <Box id={'proof-of-impact'}>
                <TextWithImageGoingOffScreen
                    title={'Share your impact with confidence'}
                    paragraph={`Every purchase comes with a certificate and project impact summaries. Logistics providers can generate certificates in their customer's name automatically, and give each customer a sustainability page that shows their impact.`}
                    img={SustyPage}
                    imgAlt={
                        'Example Lune sustainability page for Acme Inc. showing tonnes of CO₂e offset and the climate projects funded.'
                    }
                />
            </Box>
            <IllustratedMainLinks
                centered
                tiles={[
                    {
                        image: '/illustrations/menu-tile-images/stamp-gryn.png',
                        title: 'For shippers',
                        description: 'Inset your logistics emissions and offset what remains.',
                        href: '/gryn-for-shippers',
                        imageAlt: 'Gryn illustration',
                    },
                    {
                        image: '/illustrations/menu-tile-images/stamp-logistics.png',
                        title: 'For logistics providers',
                        description: 'Offer insets and offsets to your customers, in their name.',
                        href: '/logistics',
                        imageAlt: 'Logistics illustration',
                    },
                ]}
            />
            <CarbonProjectsFaq />
            <RequestDemo text={'Reduce what you can. Address the rest with integrity.'} />
        </>
    )
}
